// STEP OVERLAY-CONTROL-UX-1 · 판매자 NOTICE 보관함 shared hook.
// Studio (SellerLiveControl inline) + Console (SellerLiveConsole modal) 두 shell 이
// 동일한 CRUD 상태를 공유하도록 single source of truth.
//
// Canonical contract:
//   GET    /api/seller/notices                 → [{notice_id, text, image_url, created_at}]
//   POST   /api/seller/notices  { text?, image_url? }
//   PUT    /api/seller/notices/{notice_id}  { text?, image_url? }
//   DELETE /api/seller/notices/{notice_id}
//   POST   /api/uploads  (form: file + kind='broadcast')  — 이미지 업로드 REUSE
//
// 금지:
//   · 신규 upload endpoint 생성 (/uploads kind=broadcast REUSE)
//   · 신규 realtime channel (현재 LIVE NOTICE 송출 → streaming_sessions REUSE)
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

const useNoticeLibrary = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/seller/notices');
      setItems(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || '공지 보관함을 불러오지 못했어요.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // STEP OVERLAY-CONTROL-UX-1 · 신규 이미지 업로드 — 기존 /uploads kind=broadcast REUSE.
  const uploadImage = useCallback(async (file) => {
    if (!file) return null;
    if (!/^image\//.test(file.type)) { toast.error('이미지 파일만 업로드 가능해요.'); return null; }
    if (file.size > 5 * 1024 * 1024) { toast.error('이미지 크기는 5MB 이하여야 해요.'); return null; }
    const form = new FormData();
    form.append('file', file);
    form.append('kind', 'broadcast');
    try {
      const { data } = await api.post('/uploads', form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      return data?.url || null;
    } catch (e) {
      toast.error(e?.response?.data?.detail || '이미지 업로드 실패');
      return null;
    }
  }, []);

  const create = useCallback(async ({ text = '', image_url = '' }) => {
    const t = (text || '').trim();
    const u = (image_url || '').trim();
    if (!t && !u) { toast.error('공지 문구 또는 이미지를 입력해주세요.'); return null; }
    if (busy) return null;
    setBusy(true);
    try {
      const { data } = await api.post('/seller/notices', { text: t, image_url: u });
      setItems((prev) => [data, ...prev]);
      toast.success('공지 보관함에 저장했어요.');
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || '보관함 저장 실패');
      return null;
    } finally { setBusy(false); }
  }, [busy]);

  const update = useCallback(async (notice_id, { text = '', image_url = '' }) => {
    if (!notice_id || busy) return null;
    setBusy(true);
    try {
      const { data } = await api.put(`/seller/notices/${notice_id}`,
        { text: (text || '').trim(), image_url: (image_url || '').trim() });
      setItems((prev) => prev.map((n) => (n.notice_id === notice_id ? data : n)));
      toast.success('공지를 수정했어요.');
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || '수정 실패');
      return null;
    } finally { setBusy(false); }
  }, [busy]);

  const remove = useCallback(async (notice_id) => {
    if (!notice_id || busy) return false;
    if (!window.confirm('이 공지를 보관함에서 삭제할까요?')) return false;
    setBusy(true);
    try {
      await api.delete(`/seller/notices/${notice_id}`);
      setItems((prev) => prev.filter((n) => n.notice_id !== notice_id));
      toast.info('공지를 삭제했어요.');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || '삭제 실패');
      return false;
    } finally { setBusy(false); }
  }, [busy]);

  return { items, loading, busy, refresh, uploadImage, create, update, remove };
};

export default useNoticeLibrary;
